/**
 * Retrieves the translation of text.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-i18n/
 */
import { __ } from '@wordpress/i18n';
import { useState } from 'react';
import { SelectControl } from '@wordpress/components';
import { __experimentalNumberControl as NumberControl } from '@wordpress/components';

/**
 * Imports the InspectorControls component, which is used to wrap
 * the block's custom controls that will appear in in the Settings
 * Sidebar when the block is selected.
 *
 * Also imports the React hook that is used to mark the block wrapper
 * element. It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#inspectorcontrols
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { InspectorControls, useBlockProps } from '@wordpress/block-editor';

/**
 * Imports the necessary components that will be used to create
 * the user interface for the block's settings.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/components/panel/#panelbody
 * @see https://developer.wordpress.org/block-editor/reference-guides/components/text-control/
 * @see https://developer.wordpress.org/block-editor/reference-guides/components/toggle-control/
 */
import { PanelBody, TextControl, ToggleControl } from '@wordpress/components';

/**
 * Imports the useEffect React Hook. This is used to set an attribute when the
 * block is loaded in the Editor.
 *
 * @see https://react.dev/reference/react/useEffect
 */
import { useEffect } from 'react';

const CAMPAIGN_OPTIONS = charitable_block_data;

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @param {Object}   props               Properties passed to the function.
 * @param {Object}   props.attributes    Available block attributes.
 * @param {Function} props.setAttributes Function that updates individual attributes.
 *
 * @return {Element} Element to render.
 */
export default function Edit( { attributes, setAttributes } ) {
	const { donorsNumber, orderby, order, campaignID, distinctDonors, orientation, showName, showLocation, showAmount, showAvatar, hideIfNoDonors } = attributes;

	useEffect( () => {
		if ( donorsNumber === undefined || donorsNumber === null || donorsNumber === '' ) {
			setAttributes( { donorsNumber: 10 } );
		}
	}, [ donorsNumber, setAttributes ] );

	useEffect( () => {
		if ( orderby === undefined || orderby === null || orderby === '' ) {
			setAttributes( { orderby: 'date' } );
		}
	}, [ orderby, setAttributes ] );

	useEffect( () => {
		if ( order === undefined || order === null || order === '' ) {
			setAttributes( { order: 'DESC' } );
		}
	}, [ order, setAttributes ] );

	useEffect( () => {
		if ( showName === undefined || showName === null || showName === '' ) {
			setAttributes( { showName: 1 } );
		}
	}, [ showName, setAttributes ] );

	useEffect( () => {
		if ( showAmount === undefined || showAmount === null || showAmount === '' ) {
			setAttributes( { showAmount: 1 } );
		}
	}, [ showAmount, setAttributes ] );

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Settings', 'charitable' ) }
				>
					<NumberControl
						label={ __(
							'Number of Donors',
							'charitable'
						) }
						value={ donorsNumber }
						min={ 1 }
						onChange={ ( value ) =>
							setAttributes( { donorsNumber: value } )
						}
					/>
					<SelectControl
						label={ __(
							'Order By',
							'charitable'
						) }
						value={ orderby }
						options={ [
							{ value: 'date', label: 'Last Donation Date' },
							{ value: 'donations', label: 'Number of Donations' },
							{ value: 'amount', label: 'Amount of Money Raised' },
						] }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
					/>
					<SelectControl
						label={ __(
							'Order',
							'charitable'
						) }
						value={ order }
						options={ [
							{ value: 'DESC', label: 'DESC' },
							{ value: 'ASC', label: 'ASC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order: value } )
						}
					/>
					<SelectControl
						label={ __(
							'Campaign',
							'charitable'
						) }
						help={ __(
							'Show a particular campaign’s form, or choose no campaign to show a donation form for the current campaign on individual campaign pages (the widget will be hidden on all other pages).',
							'charitable'
						) }
						value={ campaignID }
						options= { charitable_block_data.campaigns }
						onChange={ ( value ) =>
							setAttributes( { campaignID: value } )
						}
					/>
					<ToggleControl
						checked={ distinctDonors }
						label={ __(
							'Only Show Unique Donors',
							'charitable'
						) }
						help={ __(
							'If a donor has donated more than once, they will only appear in the list once, with their donation amount calculated based on all their donations.',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								distinctDonors: ! distinctDonors,
							} )
						}
					/>
					<SelectControl
						label={ __(
							'Orientation',
							'charitable'
						) }
						value={ orientation }
						options={ [
							{ value: 'horizontal', label: 'Horizontal' },
							{ value: 'vertical', label: 'Vertical' },
						] }
						onChange={ ( value ) =>
							setAttributes( { orientation: value } )
						}
					/>
					<ToggleControl
						checked={ showName }
						label={ __(
							'Show Name',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								showName: ! showName,
							} )
						}
					/>
					<ToggleControl
						checked={ showLocation }
						label={ __(
							'Show Location',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								showLocation: ! showLocation,
							} )
						}
					/>
					<ToggleControl
						checked={ showAmount }
						label={ __(
							'Show Amount',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								showAmount: ! showAmount,
							} )
						}
					/>
					<ToggleControl
						checked={ showAvatar }
						label={ __(
							'Show Avatar',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								showAvatar: ! showAvatar,
							} )
						}
					/>
					<ToggleControl
						checked={ hideIfNoDonors }
						label={ __(
							'Hide If No Donors',
							'charitable'
						) }
						onChange={ () =>
							setAttributes( {
								hideIfNoDonors: ! hideIfNoDonors,
							} )
						}
					/>
					<p className="wpchar-gutenberg-panel-notice">
						<strong>{ charitable_block_data.panel_my_donors_notice_head }</strong>
						{ charitable_block_data.panel_my_donors_notice_text }
						<a href={ charitable_block_data.panel_my_donors_notice_link } rel="noreferrer" target="_blank">{ charitable_block_data.panel_my_donors_notice_link_text }</a>
					</p>


				</PanelBody>
			</InspectorControls>
			<div { ...useBlockProps() }>
				<div
					className="charitable-block charitable-logo">
					<img src={ charitable_block_data.logo } />
				</div>
				<h5>{ __( 'Donors Block', 'charitable' ) }</h5>
				<p>{ __( 'Preview Not Available In Editor', 'charitable' ) }</p>
			</div>
		</>
	);
}
